import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Copy,
  Play,
  Pause,
  Phone,
  Info,
} from "lucide-react";
import { format, getDate, getTime } from "date-fns";
import { toast } from "sonner";
import { convertSeconds } from "@/utils/generateTimeSlots";
import { LastCall, useLastCallStore } from "@/stores/lastCallStore";
import { formatPhoneNumber } from "@/utils/phoneNumber";
import axiosInstance from "@/utils/axiosInstance";
import { useUserContext } from "@/context/UserContext";
import { useGetList, useRefresh } from "react-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

interface Call {
  status: "answered" | "missed" | "outgoing";
  sessionId: string;
  direction: "incoming" | "outgoing";
  number: string;
  time: Date;
}

// Enhanced interface to support old softphone call logs
interface OldCallHistory {
  id: string;
  disposition?: string;
  src: string;
  cnum: string;
  dst: string;
  calldate: string;
  dcontext: string;
  duration: number;
  recordingfile?: string;
}

interface CallHistoryProps {
  calls: Call[];
  // New props to support old softphone call logs
  oldCallHistory?: OldCallHistory[];
  // Session calls from current session (from SoftPhone state)
  sessionCalls?: Array<{
    id: string;
    remote_identity?: { uri?: { user?: string }; display_name?: string };
    direction?: string;
    state?: string;
  }>;
  onCallNumber?: (phoneNumber: string) => void;
  recording_url?: string;
  apiKey?: string;
}

// Enhanced CallHistory component for recording playback
const CallRecordingPlayer = ({
  recordingfile,
  recording_url,
  apiKey,
}: {
  recordingfile?: string;
  recording_url?: string;
  apiKey?: string;
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const handleRecord = () => {
    if (!isPlaying) audioRef.current?.play();
    else audioRef.current?.pause();
    setIsPlaying((prev) => !prev);
  };

  if (!recordingfile || !recording_url || !apiKey) return null;

  // Remove any trailing query parameters from recording_url to avoid duplicates
  const baseUrl = recording_url.split("?")[0];

  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="ghost"
        onClick={handleRecord}
        className="h-6 w-6 opacity-60 hover:opacity-100 p-0"
      >
        {isPlaying ? (
          <Pause className="h-3 w-3" />
        ) : (
          <Play className="h-3 w-3" />
        )}
      </Button>
      <audio
        ref={audioRef}
        src={`${baseUrl}?file=${recordingfile}&apikey=${apiKey}`}
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
};

// Define types to fix TypeScript issues
interface CombinedCall {
  id: string;
  number: string;
  time: Date;
  direction: "incoming" | "outgoing";
  status: "answered" | "missed" | "outgoing";
  type: "new" | "session" | "database";
  duration?: number;
  recordingfile?: string;
  disposition?: string;
  originalCall?: OldCallHistory;
}

export function CallHistory({
  calls,
  oldCallHistory = [],
  sessionCalls = [],
  onCallNumber,
  recording_url,
  apiKey,
}: CallHistoryProps) {
  const refresh = useRefresh();
  const { decodedToken } = useUserContext();
  const { lastCall } = useLastCallStore();
  const { data: dbCalls = [] } = useGetList("call-logs/db");
  const [isInfo, setInfo] = useState(false);
  const [loading, setLoading] = useState(false);

  // Combine and sort all calls
  const allCalls = React.useMemo<CombinedCall[]>(() => {
    const combinedCalls: CombinedCall[] = [
      // Convert new format calls (from SoftPhone library)
      ...calls.map((call) => ({
        id: call.sessionId,
        number: call.number,
        time: call.time,
        direction: call.direction,
        status: call.status,
        type: "new" as const,
      })),
      // Convert session calls (from current session - latest calls)
      ...sessionCalls.map((call) => {
        const callNumber =
          call.remote_identity?.uri?.user ||
          call.remote_identity?.display_name ||
          "Unknown";
        const direction =
          call.direction === "outgoing"
            ? ("outgoing" as const)
            : ("incoming" as const);

        return {
          id: `session-${call.id}`,
          number: callNumber,
          time: new Date(), // Current time for session calls
          direction: direction,
          status:
            call.state === "terminated"
              ? ("answered" as const)
              : ("outgoing" as const),
          type: "session" as const,
        };
      }),
      // Convert old format calls (from GraphQL/database) - Fix timezone here
      ...oldCallHistory.map((call, index) => {
        // Parse the calldate and handle timezone properly
        // Assume the database time is in UTC and convert to local time
        const callTime = new Date(call.calldate);

        return {
          id: `database-${call.id}-${index}`,
          number: call.dcontext === "from-internal" ? call.dst : call.cnum,
          time: callTime,
          direction:
            call.dcontext === "from-internal"
              ? ("outgoing" as const)
              : ("incoming" as const),
          status:
            call.disposition === "NO ANSWER"
              ? ("missed" as const)
              : call.disposition === "ANSWERED"
                ? ("answered" as const)
                : ("outgoing" as const),
          type: "database" as const,
          duration: call.duration,
          recordingfile: call.recordingfile,
          disposition: call.disposition,
          originalCall: call,
        };
      }),
    ];

    // Sort by time (newest first)
    return combinedCalls.sort((a, b) => b.time.getTime() - a.time.getTime());
  }, [calls, oldCallHistory, sessionCalls]);

  // Group calls by date
  const groupedCalls = React.useMemo(() => {
    const groups: { [key: string]: CombinedCall[] } = {};

    allCalls.forEach((call) => {
      // Use user's timezone for grouping (date-fns format doesn't support timeZone option directly)
      const dateKey = format(call.time, "yyyy-MM-dd");
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(call);
    });

    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [allCalls]);

  // Separate calls by type for better organization
  const currentSessionCalls = React.useMemo(() => {
    return allCalls.filter((call) => call.type === "session");
  }, [allCalls]);

  // Early return after all hooks
  if (allCalls.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Call History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No calls yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const getCallIcon = (call: CombinedCall) => {
    if (call.direction === "outgoing") {
      return <PhoneOutgoing className="h-4 w-4 text-blue-500" />;
    }
    if (call.status === "missed") {
      return <PhoneMissed className="h-4 w-4 text-red-500" />;
    }
    return <PhoneIncoming className="h-4 w-4 text-green-500" />;
  };

  const handleCallClick = (phoneNumber: string) => {
    if (onCallNumber) {
      onCallNumber(phoneNumber);
    }
  };

  const handleCopyNumber = (phoneNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phoneNumber);
    toast.success("Number copied");
  };

  const markAsInformative = async (call: LastCall | null) => {
    if (!call) return;
    setLoading(true);
    try {
      const { id, number, direction, time } = call;
      const duration_seconds = Math.floor(
        (getTime(new Date()) - getTime(new Date(time))) / 1000,
      );

      await axiosInstance.post("/call-logs", {
        call_sid: id,
        direction: direction === "outgoing" ? "OUTBOUND" : "INBOUND",
        client_phone: number,
        status: "COMPLETED",
        is_info_call: true,
        settings_id: decodedToken?.settings.id,
        started_at: new Date(time),
        duration_seconds,
      });

      refresh();
      toast("Call labeled as informative");
    } catch {
      toast("Something went wrong");
    } finally {
      setLoading(false);
      setInfo(false);
    }
  };

  const renderCallItem = (call: CombinedCall) => (
    <div
      key={call.id}
      className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-lg"
    >
      <div className="flex items-center space-x-3 flex-1">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
          {getCallIcon(call)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-x-0.5">
            <p className="text-sm font-medium truncate">
              {formatPhoneNumber(call.number)}
            </p>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
              onClick={(e) =>
                handleCopyNumber(formatPhoneNumber(call.number), e)
              }
              title="Copy number"
            >
              <Copy className="h-3 w-3" />
            </Button>
            {call.type === "database" &&
              call.disposition?.toUpperCase() === "ANSWERED" &&
              call.recordingfile && (
                <CallRecordingPlayer
                  recordingfile={call.recordingfile}
                  recording_url={recording_url}
                  apiKey={apiKey}
                />
              )}
            {dbCalls.find((item) => item.call_sid === call.id) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 opacity-60 cursor-default"
                title="Informative call"
              >
                <Info className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="capitalize">{call.status}</span>•
            <span>
              {call.type === "database"
                ? format(new Date(call.time.getTime()), "HH:mm")
                : format(call.time, "HH:mm")}
            </span>
            {call.type === "database" &&
              call.disposition?.toUpperCase() === "ANSWERED" &&
              call.duration && <span>• {convertSeconds(call.duration)}</span>}
            {call.type === "session" && <span>• Current Session</span>}
            {call.type === "new" && <span>• SoftPhone</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {onCallNumber && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleCallClick(call.number);
            }}
            title="Call back"
          >
            <Phone className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Call History</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-h-96 overflow-y-auto overflow-x-hidden">
        {/* Last Call Section */}
        {lastCall && (
          <div className="pb-3 border-b">
            <p className="text-xs font-semibold text-blue-600 mb-2 sticky top-0 bg-background">
              Last Call
            </p>
            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-3 flex-1">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                  {lastCall.direction === "outgoing" ? (
                    <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                  ) : (
                    <PhoneIncoming className="h-4 w-4 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-x-0.5">
                    <p className="text-sm font-semibold text-blue-900">
                      {lastCall.callerName
                        ? `+${lastCall.callerName}`
                        : formatPhoneNumber(lastCall.number)}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          formatPhoneNumber(lastCall.number),
                        );
                        toast.success("Number copied");
                      }}
                      title="Copy number"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                    {dbCalls.find((item) => item.call_sid === lastCall.id) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 opacity-60 cursor-default"
                        title="Informative call"
                      >
                        <Info className="w-3 h-3" />
                      </Button>
                    )}
                    {lastCall.recordingfile && (
                      <CallRecordingPlayer
                        recordingfile={lastCall.recordingfile}
                        recording_url={recording_url}
                        apiKey={apiKey}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-blue-700">
                    <span>
                      {lastCall.status === "answered"
                        ? "Answered"
                        : lastCall.status}
                    </span>
                    <span>{format(new Date(lastCall.time), "HH:mm")}</span>
                    {lastCall.duration && (
                      <span>• {convertSeconds(lastCall.duration)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!dbCalls.find((item) => item.call_sid === lastCall.id) &&
                  getDate(new Date(lastCall.time)) === getDate(new Date()) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => setInfo(true)}
                      title="Mark as informative"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </Button>
                  )}
                {onCallNumber && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={() => onCallNumber(lastCall.number)}
                    title="Call back"
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Current Session Calls */}
        {currentSessionCalls.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-green-600 mb-2 sticky top-0 bg-background">
              Current Session
            </p>
            <div className="space-y-2">
              {currentSessionCalls.map(renderCallItem)}
            </div>
          </div>
        )}

        {/* Recent Database Calls by Date */}
        {groupedCalls.map(([date, dateCalls]) => {
          // Filter out session calls from date groups since they're shown separately
          const nonSessionCalls = dateCalls.filter(
            (call) => call.type !== "session",
          );

          if (nonSessionCalls.length === 0) return null;

          return (
            <div key={date}>
              <p className="text-xs font-semibold text-muted-foreground mb-2 sticky top-0 bg-background">
                {format(new Date(date), "MMM dd, yyyy")}
              </p>
              <div className="space-y-2">
                {nonSessionCalls.slice(0, 30).map(renderCallItem)}
              </div>
            </div>
          );
        })}
      </CardContent>
      <Dialog open={isInfo} onOpenChange={setInfo}>
        <DialogContent>
          <DialogTitle>Call label</DialogTitle>
          <DialogDescription>
            Are you sure you want to mark this call as informative? You cannot
            change it later
          </DialogDescription>
          <div className="flex justify-end gap-x-2">
            <Button variant="outline" onClick={() => setInfo(false)}>
              No
            </Button>
            <Button
              onClick={() => markAsInformative(lastCall)}
              disabled={loading}
            >
              {loading ? "Confirming..." : "Yes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
